import { featureGroupDefinitions } from './feature-group-definitions';
import { Features } from '../../../../../../../constants/features';
import { isStandardPlan, isPremiumPlan } from '../../../../../../../enums/billing-plan-ids';

/**
 * @typedef {import('react-intl').MessageDescriptor} MessageDescriptor
 */

/**
 * @typedef {import('../../new-lineup-plan-grid-types.d.ts').FeatureGroupDefinition} FeatureGroupDefinition
 */

/**
 * @typedef {import('../../new-lineup-plan-grid-types.d.ts').FeatureDefinition} FeatureDefinition
 */

/**
 * @typedef {import('../../new-lineup-plan-grid-types.d.ts').FeatureGroup} FeatureGroup
 */

/**
 * @typedef {import('../../new-lineup-plan-grid-types.d.ts').Feature} Feature
 */

/**
 * @typedef {import('../../../../../../../prop-types/subscription-data-types.d.ts').BillingPlan} BillingPlan
 */

/**
 * @typedef {import('../../../../../../../prop-types/subscription-data-types.d.ts').SubscriptionData} SubscriptionData
 */

/**
 * @typedef {Record<string, boolean>} FeatureLookup
 */

/**
 * @typedef {Record<string, FeatureLookup>} PlanFeatureLookup
 */

/**
 * @param {BillingPlan} plan
 * @returns {FeatureLookup}
 *   example output:
 *   {
 *      'featureId1': true,
 *      'featureId2': false,
 *      'featureId3': true,
 *   }
 */
const createFeatureLookupForPlan = (plan) => {
  const { subscriptionLineItems, billingPlanId } = plan;

  const planFeatureMap = {};

  subscriptionLineItems.forEach((lineItem) => {
    const { featureIds, isIncludedInPlan } = lineItem;

    featureIds.forEach((featureId) => {
      planFeatureMap[featureId] = planFeatureMap[featureId] || isIncludedInPlan;
    });
  });

  // TEMPORARY HACK Because of shims on shims on shims
  if (planFeatureMap[Features.NonWalletProviderAccess]) {
    planFeatureMap[Features.NonWalletProviderAccess] = isStandardPlan(billingPlanId) || isPremiumPlan(billingPlanId);
  }

  return planFeatureMap;
};

/**
 *
 * @param {PlanFeatureLookup} planFeatureLookup
 * @param {FeatureDefinition} feature
 * @returns {Feature}
 *   example output:
 *   {
 *     featureId: 'featureId1',
 *     featureNameMessageDescriptor: {id: 'abc', defaultMessage: 'Feature Name'},
 *     planAvailability: [
 *      { billingPlanId: 'planId1', isIncludedInPlan: false },
 *      { billingPlanId: 'planId2', isIncludedInPlan: false },
 *      { billingPlanId: 'planId3', isIncludedInPlan: true },
 *     ],
 *   }
 */
const createFeatureRowData = (planFeatureLookup, feature) => {
  const featureId = feature.id;

  // Lookup the feature availability for each plan to create row data
  const planAvailability = Object.entries(planFeatureLookup).map(([planId, planFeatures]) => {
    return {
      billingPlanId: planId,
      isIncludedInPlan: planFeatures[featureId],
    };
  }, {});

  return {
    featureId,
    featureNameMessageDescriptor: feature.messageDescriptor,
    planAvailability,
  };
};

/**
 * @param {PlanFeatureLookup} planFeatureLookup
 * @param {FeatureGroupDefinition} featureGroupDefinition
 * @returns {FeatureGroup}
 *   example output:
 *   {
 *     featureGroupId: 'featureGroupId1',
 *     featureGroupNameMessageDescriptor: {id: 'abc', defaultMessage: 'Feature Group Name'},
 *     features: [
 *      {
 *        featureId: 'featureId1',
 *        featureNameMessageDescriptor: {id: 'abc', defaultMessage: 'Feature Name'},
 *        planAvailability: [
 *          { billingPlanId: 'planId1', isIncludedInPlan: false },
 *          { billingPlanId: 'planId2', isIncludedInPlan: false },
 *          { billingPlanId: 'planId3', isIncludedInPlan: true },
 *        ],
 *      }
 *     ],
 *   }
 */
const createFeatureGroupData = (planFeatureLookup, featureGroupDefinition) => {
  const { id: featureGroupId, nameMessageDescriptor, features } = featureGroupDefinition;

  // populate feature availability for each feature in the feature group
  const featuresAvailabilityByPlan = features.map((feature) => createFeatureRowData(planFeatureLookup, feature));

  return {
    featureGroupId,
    featureGroupNameMessageDescriptor: nameMessageDescriptor,
    features: featuresAvailabilityByPlan,
  };
};

/**
 * @param {SubscriptionData} subscriptionData
 * @returns {FeatureGroup[]}
 * example output:
 *  [
 *    {
 *      featureGroupId: 'featureGroupId1',
 *      featureGroupNameMessageDescriptor: {id: 'abc', defaultMessage: 'Feature Group Name'},
 *      features: [
 *       {
 *         featureId: 'featureId1',
 *         featureNameMessageDescriptor: {id: 'abc', defaultMessage: 'Feature Name'},
 *         planAvailability: [
 *           { billingPlanId: 'planId1', isIncludedInPlan: false },
 *           { billingPlanId: 'planId2', isIncludedInPlan: false },
 *           { billingPlanId: 'planId3', isIncludedInPlan: true },
 *         ],
 *       }
 *      ],
 *    }
 *  ]
 */
export const createFeatureGridData = (subscriptionData) => {
  // create plan feature lookup
  const planFeatureLookup = subscriptionData.allPlans.reduce((acc, plan) => {
    const { billingPlanId } = plan;
    const planFeatures = createFeatureLookupForPlan(plan);

    return {
      ...acc,
      [billingPlanId]: {
        ...planFeatures,
      },
    };
  }, {});

  // populate feature availability for each feature group
  return featureGroupDefinitions.map((featureGroupDefinition) => {
    return createFeatureGroupData(planFeatureLookup, featureGroupDefinition);
  });
};
